import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { BrandAccountComponent } from './molecules/brand-account/brand-account.component';
import { BrandTeamComponent } from './molecules/brand-team/brand-team.component';
import { BrandBillingComponent } from './molecules/brand-billing/brand-billing.component';
import { BrandProductsComponent } from './molecules/brand-products/brand-products.component';
import { ComplianceComponent } from './molecules/compliance/compliance.component';
import { AffiliateMarketingComponent } from './molecules/affiliate-marketing/affiliate-marketing.component';
import { BrandTikTokConnectComponent } from './molecules/brand-tiktok-connect/brand-tiktok-connect.component';
import { ContentLibraryComponent } from './molecules/content-library/content-library';

export type MyBrandTab =
  | 'business-info'
  | 'team-members'
  | 'products'
  | 'content-library'
  | 'billing'
  | 'compliance'
  | 'affiliate-marketing'
  | 'social-ads';

export const DEFAULT_MY_BRAND_TAB: MyBrandTab = 'business-info';

const MY_BRAND_TABS: readonly MyBrandTab[] = [
  'business-info',
  'products',
  'content-library',
  'team-members',
  'compliance',
  'affiliate-marketing',
  'social-ads',
  'billing',
];

@Component({
  selector: 'app-my-brand',
  standalone: true,
  imports: [
    RouterLink,
    BrandAccountComponent,
    BrandProductsComponent,
    BrandTeamComponent,
    BrandBillingComponent,
    ComplianceComponent,
    AffiliateMarketingComponent,
    BrandTikTokConnectComponent,
    ContentLibraryComponent,
  ],
  templateUrl: './my-brand.component.html',
  styleUrl: './my-brand.component.css',
})
export class MyBrandComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public readonly currentUserService = inject(CurrentUserService);

  readonly tabs: { id: MyBrandTab; label: string; mobileLabel: string; billingOnly?: boolean }[] = [
    { id: 'business-info', label: 'Business Info', mobileLabel: 'Business Info' },
    { id: 'products', label: 'Products', mobileLabel: 'Products' },
    { id: 'content-library', label: 'Content Library', mobileLabel: 'Content Library', billingOnly: true },
    { id: 'team-members', label: 'Team Members', mobileLabel: 'Team Members' },
    { id: 'compliance', label: 'Compliance Library', mobileLabel: 'Compliance', billingOnly: true },
    { id: 'social-ads', label: 'Social Ads', mobileLabel: 'Social Ads' },
    { id: 'billing', label: 'Billing', mobileLabel: 'Billing', billingOnly: true },
  ];

  private readonly tabParam = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('tab'))),
    { initialValue: this.route.snapshot.paramMap.get('tab') },
  );

  readonly selectedTab = computed<MyBrandTab>(() => {
    const tab = this.tabParam();
    return tab && this.isMyBrandTab(tab) ? tab : DEFAULT_MY_BRAND_TAB;
  });

  ngOnInit(): void {
    const queryTab = this.route.snapshot.queryParamMap.get('tab');
    if (queryTab && this.isMyBrandTab(queryTab) && !this.shouldBlockBilling(queryTab)) {
      void this.navigateToTab(queryTab, true);
      return;
    }

    const tab = this.tabParam();
    if (!tab || !this.isMyBrandTab(tab) || this.shouldBlockBilling(tab)) {
      void this.navigateToTab(DEFAULT_MY_BRAND_TAB, true);
    }
  }

  canShowBillingTab(): boolean {
    return (
      this.currentUserService.canAccessSandboxBilling() &&
      !!this.currentUserService.myAgencyies &&
      this.currentUserService.myAgencyies.length === 0
    );
  }

  selectTabMobile(event: Event): void {
    this.selectTab((event.target as HTMLSelectElement).value as MyBrandTab);
  }

  selectTab(tab: MyBrandTab): void {
    void this.navigateToTab(tab, false);
  }

  getTabClass(tab: MyBrandTab): string {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  tabVisible(tab: { billingOnly?: boolean }): boolean {
    return !tab.billingOnly || this.canShowBillingTab();
  }

  private shouldBlockBilling(tab: string): boolean {
    return (
      tab === 'billing' &&
      this.currentUserService.isSandboxUser() &&
      !this.currentUserService.canAccessSandboxBilling()
    );
  }

  private navigateToTab(tab: MyBrandTab, replaceUrl: boolean): Promise<boolean> {
    return this.router.navigate(['/my-brand', tab], {
      replaceUrl,
      queryParams: {},
    });
  }

  private isMyBrandTab(value: string): value is MyBrandTab {
    return (MY_BRAND_TABS as readonly string[]).includes(value);
  }
}
